// ── V158: 입고 탭 ────────────────────────────────────────────────────────────
// 사장님: "발주서는 실제 그 물건이 구매되고 입고되어 비용으로 처리된 자료는 아니다.
//          단가표에 입력된 발주처마다 각각 매일 어떤 식재료가 실제 입고되었는지 기록이 되어야 해."
//          "발주한 것을 클릭하면 자동으로 입고 목록과 수량이 채워지고 수정하는 방식"
//          "외상 잔액 관리 필요해. 영수증 사진 저장도 바로 진행"
//   📥 입고하기 — 입고 대기 발주서를 누르면 품목·수량·단가가 채워진다 → 영수증 보면서 받은 대로 고친다
//                 (같은 발주처 발주서 여러 장을 함께 · 발주서 없이 바로 입고도 된다)
//                 단가가 단가표와 다르면 ▲▼ 표시 → 저장할 때 단가표에도 반영 (기본 켜짐, 끌 수 있음)
//                 결제: 현금 · 위챗 · 알리페이 · 카드 · 계좌이체 · 외상 / 영수증 사진 / 영수증 합계와 비교
//   📚 내역 — 달별 · 발주처별 합계, 영수증 한 장씩 (사진 보기 · 고치기 · 지우기)
//   📒 외상 — 발주처별 갚을 돈, 고른 영수증 정산 (방법 · 날짜), 정산 되돌리기
// 손익의 식재료비는 그 달 입고 합계 (ProfitLoss). 계산·저장 모양은 utils/receiving.js
// ── V159 ── 계정과목: 줄마다 식재료 · 주류·음료 · 소모품 · 연료(숯·가스) · 수선·시설 · 비품 · 기타
//   단가표 품목은 그 품목의 계정과목이 따라오고, 줄에서 바꿀 수 있다. 손익에는 계정과목마다 따로 줄로 들어간다
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLang } from '../i18n/LangContext';
import BiName, { buildZhMap, zhOf } from '../i18n/BiName';
import { unitToZh, specToZh } from '../i18n/units';
import { searchVariants } from '../utils/hangulKeys';
import { todayKey } from '../utils/workCalc';
import { orderKeyOf } from '../utils/orderKey';
import { orderPackInfo } from '../utils/orderPack';
import {
  PAY_METHODS, PAY_LABEL, SETTLE_METHODS, PENDING_DAYS, MEMO_MAX,
  vendorsOf, lineFromRow, newItemLine, pendingOrders, orderVendor, orderOpenItems, linesFromOrders,
  lineQty, linePrice, lineAmount, sumLines, priceDiff, priceUpdateCandidates, applyPriceUpdates, linkNewRows,
  buildReceivingDoc, linesFromReceiving, ordersAfterReceive, ordersAfterDelete, orderSkipped,
  creditBalances, settlePatch, unsettlePatch, monthSummary, lastPaymentOf,
  ACCTS, ACCT_LABEL, acctOfLine, acctAmounts,
  findSameNameRow, rowGaps, suggestNetG, applyRowFixes, okeyOf,
  searchReceivings, searchOrders, searchPriceRows, nameKey,   // V163 품목 검색
} from '../utils/receiving';
import { compressReceiptPhoto } from '../utils/photoCompress';
import { translateOne, translateMemo } from '../utils/translate';   // V161 중국어 ↔ 한국어
import { koNameOrSound } from '../utils/koNameFill';   // V162 번역이 안 되면 병음 한글
import { splitKoZh, nameParts, hasHangul, hasCjk } from '../utils/koZh';

// V161: 번역 (테스트에서 바꿔 끼울 수 있게)
const DEFAULT_TR = { toKo: (zh) => koNameOrSound(zh), toZh: (ko) => translateOne(ko, 'item'), memo: (t) => translateMemo(t) };

// V162: 중국어로만 저장된 입고 줄 — 단가표의 그 품목(코드)이 나중에 한국어 이름을 얻었으면 그 이름으로 보여 준다
const codeOfItem = (it) => String(it?.code || '').trim() || String(it?.key || '').split('::')[0];
export const rowsByCode = (prices) => { const m = new Map(); for (const p of prices || []) if (p && p.code && !m.has(p.code)) m.set(String(p.code), p); return m; };
export function shownParts(it, byCode) {
  const np = nameParts(it);
  if (hasHangul(np.ko) || !hasCjk(np.ko) || !byCode) return np;
  const row = byCode.get(codeOfItem(it));
  if (!row || !hasHangul(row.name)) return np;
  const rp = nameParts(row);
  return { ko: rp.ko, zh: rp.zh || np.ko };
}

// V161: 단가표 반영 체크 — 새 입고는 기본 켜짐, 저장된 입고를 고칠 때는 원래 있던 줄은 기본 꺼짐
//   (고쳐서 다시 저장할 때마다 같은 품목이 단가표에 또 들어가던 문제)
const isOrigLine = (d, id) => !!(d && d.prev && (d.origIds || []).includes(id));
export const puOn = (d, id) => (isOrigLine(d, id) ? (d.priceOn || []).includes(id) : !(d.priceOff || []).includes(id));
export const puToggle = (d, id, on) => (isOrigLine(d, id)
  ? { priceOn: on ? (d.priceOn || []).filter(x => x !== id) : [...(d.priceOn || []), id] }
  : { priceOff: on ? [...(d.priceOff || []), id] : (d.priceOff || []).filter(x => x !== id) });

// ── V172: 입고 화면 한·중 병기 ───────────────────────────────────────────────
// 사장님: "입고 내역 작성할 때 중국어 버전에 중국어만 있어서 내가 설명하기가 힘들어. 한국어로 바꾸면 기록도 사라지고,
//          한국어 버전 중국어 모드 둘 다 양쪽 언어 병행해 줘"
//   → 어느 언어로 보든 보는 언어를 앞에, 다른 언어를 뒤에 작게(긴 문장은 다음 줄에). 알림·확인 창은 두 줄.
//     품목 · 발주처 · 계정과목 · 결제 방법도 둘 다. 단위 · 규격 · 날짜는 보는 언어로만 (斤/근처럼 짧고 숫자에 붙어 있어서)
//   → 언어를 바꾸면 App이 화면을 통째로 다시 그려서 쓰던 입고가 사라졌다 → KEEP에 들고 있다가 이어서 보여 준다
const SUB_IN = { fontSize: '0.86em', fontWeight: 500, opacity: 0.75, marginLeft: 4 };
const SUB_BLOCK = { display: 'block', fontSize: '0.9em', fontWeight: 500, opacity: 0.75, marginTop: 1 };
const str = (v) => (v == null ? '' : String(v));
/**
 * 보는 언어(zh)에 맞춘 병기 도구.  Z(ko, zh) 알림·확인 창용 두 줄 문자열
 *   Z.P(ko, zh) 한 줄 문자열 (입력칸 안내 · 고르기 칸)   Z.B(ko, zh, block?) 화면 (짧으면 옆에, 길면 다음 줄에 작게)
 *   Z.one(ko, zh) 보는 언어 하나만
 */
export function makeBi(zh) {
  const pair = (ko, z) => (zh ? [str(z), str(ko)] : [str(ko), str(z)]);
  const same = (a, b) => !b.trim() || a.trim() === b.trim();
  const Z = (ko, z) => { const [a, b] = pair(ko, z); return same(a, b) ? a : `${a}\n${b.trim()}`; };
  Z.P = (ko, z) => { const [a, b] = pair(ko, z); return same(a, b) ? a : `${a} · ${b.trim()}`; };
  Z.B = (ko, z, block) => {
    const [a, b] = pair(ko, z);
    if (same(a, b)) return a;
    const blk = block != null ? block : (a.trim().length + b.trim().length > 24);
    return <>{a}<span data-bi2 style={blk ? SUB_BLOCK : SUB_IN}>{b.trim()}</span></>;
  };
  Z.one = (ko, z) => (zh ? z : ko);
  Z.zh = zh;
  return Z;
}
/** 품목 이름 둘 다 — 한 줄 문자열 (발주서 요약 · 안 온 품목) */
export const nameBoth = (zh, ko, z) => {
  const k = str(ko).trim(); const c = str(z).trim();
  const a = zh ? (c || k) : k; const b = zh ? (c ? k : '') : c;
  return b && b !== a ? `${a}/${b}` : a;
};
/** 품목 이름 둘 다 — 화면 */
function NameBi({ np, zh }) {
  const a = zh ? (np.zh || np.ko) : np.ko;
  const b = zh ? (np.zh ? np.ko : '') : np.zh;
  return <>{a}{b && b !== a ? <span data-bi2 style={SUB_IN}>{b}</span> : null}</>;
}
// 언어를 바꿔 화면이 새로 그려져도(다른 탭에 갔다 와도) 같은 매장이면 쓰던 입고 · 보던 탭을 이어서
export const KEEP = { store: null, draft: null, tab: 'new' };
/** 이어서 열 때 — 번역 중이던 줄은 Editor가 다시 번역한다 (retr 표시) */
const reviveDraft = (d) => (d && Array.isArray(d.lines)
  ? { ...d, lines: d.lines.map(l => (l.koPending || l.zhPending ? { ...l, retr: true } : l)) } : d);

const ORANGE = '#FF6F0F';
const BLUE = '#1A56A0';
const RED = '#DC2626';
const GREEN = '#15803D';
const card = { border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff' };
const btn = (on, color = ORANGE) => ({
  padding: '7px 12px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
  border: on ? `1.5px solid ${color}` : '1px solid #d1d5db', background: on ? color : '#fff', color: on ? '#fff' : '#18181B',
});
const chip = (on, color = ORANGE) => ({
  padding: '6px 11px', borderRadius: 999, fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
  border: on ? `1.5px solid ${color}` : '1px solid #d1d5db', background: on ? color : '#fff', color: on ? '#fff' : '#3f3f46',
});
const numIn = { width: 74, padding: '7px 6px', borderRadius: 7, border: '1px solid #d1d5db', fontSize: 15, textAlign: 'right' };
const r3 = (n) => Math.round((Number(n) || 0) * 1000) / 1000;
const hhmm = (ms) => { if (!ms) return ''; const d = new Date(ms); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };
const monthShift = (m, n) => { const [y, mo] = m.split('-').map(Number); const d = new Date(y, mo - 1 + n, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
const dayLabel = (key, zh) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(key || ''));
  if (!m) return key || '';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const w = zh ? '日一二三四五六'[d.getDay()] : '일월화수목금토'[d.getDay()];
  return zh ? `${Number(m[2])}月${Number(m[3])}日 (${w})` : `${Number(m[2])}월 ${Number(m[3])}일 (${w})`;
};

export default function ReceivingPage({
  prices = [], orderHistory = [], receivings = [], loading = false, user, isMobile = false, canDelete = false,
  onSaveReceiving, onPatchReceiving, onDeleteReceiving, onSavePhoto, onLoadPhoto, onRemovePhoto,
  onPricesChange, onUpdateOrders, compressPhoto = compressReceiptPhoto, today: todayProp, translator = DEFAULT_TR, storeKey,
}) {
  const { lang, fmt: money, country } = useLang();
  const zh = lang === 'zh';
  const Z = useMemo(() => makeBi(zh), [zh]);   // V172 병기
  const { B } = Z;
  const packMode = country === 'CN';
  const today = todayProp || todayKey();
  const who = String(user?.displayName || String(user?.email || '').split('@')[0] || '').slice(0, 30);

  const zhMap = useMemo(() => buildZhMap(prices), [prices]);
  const vendors = useMemo(() => vendorsOf(prices), [prices]);
  const vendorZh = useMemo(() => new Map(vendors.map(v => [v.vendor, v.vendor_zh])), [vendors]);
  const vLabel = (v, vz) => {
    const z = vz || vendorZh.get(v) || '';
    if (!v) return Z.P('발주처 미지정', '未指定供应商');
    return z && z !== v ? (zh ? `${z} · ${v}` : `${v} · ${z}`) : v;   // V172 어느 언어든 둘 다
  };
  /** 알림 문장 안 — 그 언어 이름 하나 */
  const vOne = (v, vz, l) => (l === 'zh' ? (vz || vendorZh.get(v) || v || '未指定供应商') : (v || '발주처 미지정'));
  const U = (u) => (zh ? (unitToZh(u) || u) : u);
  const payText = (m) => { const p = PAY_LABEL[m]; return p ? <>{p.icon} {B(p.ko, p.zh)}</> : m; };
  const payOne = (m, l) => { const p = PAY_LABEL[m]; return p ? `${p.icon} ${l === 'zh' ? p.zh : p.ko}` : m; };

  const sk = storeKey != null ? String(storeKey) : String(user?.uid || '');
  const kept = KEEP.store === sk ? KEEP : null;      // V172 언어를 바꿔도 이어서
  const [tab, setTab] = useState(() => (kept ? kept.tab || 'new' : 'new'));            // new | history | credit
  const [draft, setDraft] = useState(() => (kept ? reviveDraft(kept.draft) : null));          // 편집 중인 입고
  useEffect(() => { KEEP.store = sk; KEEP.draft = draft; KEEP.tab = tab; }, [sk, draft, tab]);
  const [toast, setToast] = useState(null);          // { msg, kind, undo? }
  const toastTimer = useRef(null);
  const say = (msg, kind = 'ok', undo = null) => {
    clearTimeout(toastTimer.current);
    setToast({ msg, kind, undo });
    toastTimer.current = setTimeout(() => setToast(null), undo ? 7000 : 4000);
  };
  useEffect(() => () => clearTimeout(toastTimer.current), []);
  const [photoView, setPhotoView] = useState(null);  // { id, data?, loading, error }

  const credit = useMemo(() => creditBalances(receivings), [receivings]);
  const creditTotal = credit.reduce((a, c) => a + c.unpaid, 0);

  const openPhoto = async (id, data) => {
    if (data) { setPhotoView({ id, data }); return; }
    setPhotoView({ id, loading: true });
    try {
      const p = onLoadPhoto ? await onLoadPhoto(id) : null;
      setPhotoView(p?.data ? { id, data: p.data } : { id, error: Z('사진을 찾지 못했어요', '没找到照片') });
    } catch (e) {
      setPhotoView({ id, error: Z('사진을 불러오지 못했어요', '照片加载失败') });
    }
  };

  // ── 편집 시작 ─────────────────────────────────────────────────────────────
  const startFromOrders = (orders) => {
    const list = (orders || []).filter(Boolean);
    if (!list.length) return;
    const vendor = orderVendor(list[0]);
    const lines = linesFromOrders(list, prices, packMode);
    setDraft({
      vendor, vendor_zh: vendorZh.get(vendor) || '', date: today, lines, receiptTotal: '', memo: '',
      payment: lastPaymentOf(receivings, vendor) || 'cash', orderIds: list.map(h => h.id),
      photo: null, photoState: 'none', priceOff: [], priceOn: [], rowFix: {}, addOpen: false,
    });
  };
  const startFree = (vendor) => {
    const v = String(vendor || '').trim();
    if (!v) return;
    setDraft({
      vendor: v, vendor_zh: vendorZh.get(v) || '', date: today, lines: [], receiptTotal: '', memo: '',
      payment: lastPaymentOf(receivings, v) || 'cash', orderIds: [],
      photo: null, photoState: 'none', priceOff: [], priceOn: [], rowFix: {}, addOpen: true,
    });
  };
  const startEdit = (rec) => {
    const byCode = rowsByCode(prices);
    const lines = linesFromReceiving(rec).map(l => {
      const sp = shownParts(l, byCode);
      if (sp.ko === nameParts(l).ko) return l;
      const row = byCode.get(codeOfItem(l));
      return { ...l, name: row.name, name_zh: row.name_zh || l.name, key: orderKeyOf(row), okey: okeyOf(row.name, row.spec) };
    });
    setDraft({
      id: rec.id, prev: rec, vendor: rec.vendor, vendor_zh: rec.vendor_zh || vendorZh.get(rec.vendor) || '', date: rec.date,
      lines, origIds: lines.map(l => l.id), receiptTotal: rec.receiptTotal ? String(rec.receiptTotal) : '', memo: rec.memo || '',
      payment: rec.payment || 'cash', orderIds: rec.orderIds || [],
      photo: null, photoState: rec.hasPhoto ? 'keep' : 'none', priceOff: [], priceOn: [], rowFix: {}, addOpen: false,
    });
    setTab('new');
  };

  // ── 저장 ──────────────────────────────────────────────────────────────────
  const save = (d, cands) => {
    const vendor = String(d.vendor || '').trim();
    if (!vendor) { say(Z('발주처를 골라 주세요', '请选择供应商'), 'err'); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d.date || '')) { say(Z('날짜를 확인해 주세요', '请确认日期'), 'err'); return; }
    const got = d.lines.filter(l => lineQty(l) > 0);
    if (!got.length) {
      if (!d.prev && d.orderIds.length) {
        if (window.confirm(Z('받은 품목이 없어요. 이 발주서를 "입고 안 함"으로 할까요?', '没有收到任何货。把这张订货单标为"不入库"吗？'))) {
          skipOrders(orderHistory.filter(h => d.orderIds.includes(h.id)));
          setDraft(null);
        }
        return;
      }
      say(Z('받은 수량을 넣어 주세요', '请填写实收数量'), 'err');
      return;
    }
    const noPrice = got.filter(l => !(linePrice(l) > 0));
    if (noPrice.length && !window.confirm(Z(
      `단가가 없는 품목이 ${noPrice.length}개 있어요 (${noPrice.map(l => l.name).slice(0, 3).join(', ')}). 0으로 저장할까요?`,
      `有 ${noPrice.length} 项没有单价（${noPrice.map(l => l.name_zh || l.name).slice(0, 3).join('、')}）。按 0 保存吗？`,
    ))) return;

    // 1) 단가표
    let lines = d.lines;
    const chosen = new Set(cands.filter(c => puOn(d, c.line.id)).map(c => c.line.id));
    let priceMsg = '';
    let priceMsgZh = '';
    let nextPrices = null;
    if (chosen.size && onPricesChange) {
      const up = applyPriceUpdates(prices, lines, chosen, { packMode, vendor, vendor_zh: d.vendor_zh });
      if (up.changed || up.added || Object.keys(up.rowsByLine).length) {
        if (up.changed || up.added) nextPrices = up.prices;
        lines = linkNewRows(lines, up.rowsByLine);
        if (up.changed || up.added) { priceMsg = ` · 단가표 ${up.changed + up.added}개 반영`; priceMsgZh = ` · 已更新 ${up.changed + up.added} 项单价`; }
      }
    }
    // V161: 단가표에 빠져 있던 중량·단위를 입고 화면에서 채운 것
    if (onPricesChange && d.rowFix && Object.keys(d.rowFix).length) {
      const rf = applyRowFixes(nextPrices || prices, d.rowFix);
      if (rf.fixed) { nextPrices = rf.prices; priceMsg += ` · 단가표 정보 ${rf.fixed}개 채움`; priceMsgZh += ` · 已补全 ${rf.fixed} 项单价表信息`; }
    }
    if (nextPrices) onPricesChange(nextPrices);
    // 2) 입고 문서
    const hasPhoto = d.photoState === 'keep' || d.photoState === 'new';
    const rec = buildReceivingDoc({
      id: d.id, date: d.date, vendor, vendor_zh: d.vendor_zh, lines, receiptTotal: d.receiptTotal, payment: d.payment,
      memo: d.memo, orderIds: d.orderIds, hasPhoto, who, prev: d.prev || null,
    });
    // 3) 발주서 상태 (새 입고: 받은 만큼 / 고칠 때: 이 입고만 연결된 발주서는 다시 계산)
    let before = [];
    if (onUpdateOrders && rec.orderIds && rec.orderIds.length) {
      const linked = orderHistory.filter(h => rec.orderIds.includes(h.id));
      const base = d.prev
        ? linked.filter(h => !(h.receive?.ids || []).some(x => x !== rec.id)).map(h => { const { receive, ...o } = h; return o; })
        : linked;
      if (base.length) {
        before = linked.filter(h => base.some(b => b.id === h.id));
        onUpdateOrders(ordersAfterReceive(base, lines, rec.id));
      }
    }
    // 4) 보내기 — 화면은 바로 닫고, 실패하면 되돌리고 다시 연다
    const back = d;
    setDraft(null);
    say(Z(`저장 중… ${vOne(vendor, d.vendor_zh, 'ko')} ${money(rec.total)}`, `保存中… ${vOne(vendor, d.vendor_zh, 'zh')} ${money(rec.total)}`), 'busy');
    const fail = (e) => {
      console.error('receiving save', e);
      if (before.length && onUpdateOrders) onUpdateOrders(before);
      setDraft(back);
      say(Z('저장하지 못했어요. 인터넷을 확인하고 다시 눌러 주세요', '保存失败，请检查网络后再试'), 'err');
    };
    Promise.resolve(onSaveReceiving ? onSaveReceiving(rec) : null)
      .then(() => {
        if (d.photoState === 'new' && d.photo && onSavePhoto) return onSavePhoto(rec.id, d.photo);
        if (d.photoState === 'removed' && d.prev?.hasPhoto && onRemovePhoto) Promise.resolve(onRemovePhoto(rec.id)).catch(() => {});
        return null;
      })
      .then(() => {
        say(Z(`✓ 입고 저장 — ${vOne(vendor, d.vendor_zh, 'ko')} ${money(rec.total)}${rec.payment === 'credit' ? ' (외상)' : ''}${priceMsg}`,
          `✓ 已入库 — ${vOne(vendor, d.vendor_zh, 'zh')} ${money(rec.total)}${rec.payment === 'credit' ? '（赊账）' : ''}${priceMsgZh}`));
        // V161: 메모를 다른 언어로도 (한국어 메모 → 중국어, 중국어 메모 → 한국어). 실패해도 그만
        if (rec.memo && !rec.memo_zh && !rec.memo_ko && translator?.memo && onPatchReceiving) {
          Promise.resolve(translator.memo(rec.memo))
            .then(r => { if (r && r.text) return onPatchReceiving(rec.id, r.to === 'zh' ? { memo_zh: r.text } : { memo_ko: r.text }); return null; })
            .catch(() => {});
        }
      })
      .catch(fail);
  };

  const skipOrders = (orders) => {
    if (!orders.length || !onUpdateOrders) return;
    const prevs = orders.map(h => ({ ...h }));
    onUpdateOrders(orders.map(h => orderSkipped(h)));
    say(Z(`발주서 ${orders.length}장을 '입고 안 함'으로 했어요`, `已将 ${orders.length} 张订货单标为"不入库"`), 'ok',
      () => { onUpdateOrders(prevs); setToast(null); });
  };

  const deleteRec = (rec) => {
    if (!canDelete || !onDeleteReceiving) return;
    if (!window.confirm(Z(`${rec.date} ${vOne(rec.vendor, rec.vendor_zh, 'ko')} ${money(rec.total)} 입고 기록을 지울까요? (연결된 발주서는 다시 입고 대기로 돌아가요)`,
      `删除 ${rec.date} ${vOne(rec.vendor, rec.vendor_zh, 'zh')} ${money(rec.total)} 的入库记录吗？（关联的订货单会回到待入库）`))) return;
    const ups = ordersAfterDelete(orderHistory, rec);
    if (ups.length && onUpdateOrders) onUpdateOrders(ups);
    Promise.resolve(onDeleteReceiving(rec))
      .then(() => say(Z('지웠어요', '已删除')))
      .catch(() => say(Z('지우지 못했어요', '删除失败'), 'err'));
  };

  const patchRecs = (ids, fields, msg) => {
    if (!onPatchReceiving || !ids.length) return;
    Promise.all(ids.map(id => onPatchReceiving(id, fields)))
      .then(() => msg && say(msg))
      .catch(() => say(Z('저장하지 못했어요', '保存失败'), 'err'));
  };

  // ── 화면 ──────────────────────────────────────────────────────────────────
  const wrap = { padding: isMobile ? '12px 10px 90px' : '18px 22px 40px', maxWidth: 1080, margin: '0 auto' };
  return (
    <div style={wrap} data-receiving-page>
      {!draft && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
          {[
            ['new', <>📥 {B('입고하기', '入库')}</>],
            ['history', <>📚 {B('입고 내역', '入库记录')}</>],
            ['credit', <>📒 {B('외상', '赊账')}{credit.length ? ` ${money(creditTotal)}` : ''}</>],
          ].map(([k, label]) => (
            <button key={k} data-rcv-tab={k} onClick={() => setTab(k)} style={{ ...btn(tab === k), fontSize: 14, padding: '9px 14px' }}>{label}</button>
          ))}
        </div>
      )}

      {draft ? (
        <Editor
          draft={draft} setDraft={setDraft} prices={prices} receivings={receivings} packMode={packMode}
          vendors={vendors} zhMap={zhMap} Z={Z} zh={zh} U={U} money={money} vLabel={vLabel} payText={payText}
          isMobile={isMobile} compressPhoto={compressPhoto} say={say} onSave={save} openPhoto={openPhoto} translator={translator}
        />
      ) : tab === 'new' ? (
        <NewPane
          orderHistory={orderHistory} receivings={receivings} prices={prices} today={today} vendors={vendors} zhMap={zhMap}
          Z={Z} zh={zh} money={money} vLabel={vLabel} payText={payText} U={U}
          onStart={startFromOrders} onFree={startFree} onSkip={skipOrders} say={say} loading={loading}
        />
      ) : tab === 'history' ? (
        <HistoryPane
          receivings={receivings} today={today} Z={Z} zh={zh} U={U} money={money} vLabel={vLabel} payText={payText} payOne={payOne}
          zhMap={zhMap} canDelete={canDelete} onEdit={startEdit} onDelete={deleteRec} openPhoto={openPhoto} loading={loading}
          translator={translator} onPatch={onPatchReceiving} prices={prices} orderHistory={orderHistory}
        />
      ) : (
        <CreditPane
          credit={credit} receivings={receivings} today={today} Z={Z} zh={zh} money={money} vLabel={vLabel} payText={payText} vOne={vOne} payOne={payOne}
          onSettle={(ids, date, method) => patchRecs(ids, settlePatch({ date, method }),
            Z(`✓ 정산했어요 (${ids.length}건)`, `✓ 已结清（${ids.length} 笔）`))}
          onUnsettle={(id) => patchRecs([id], unsettlePatch(), Z('정산을 되돌렸어요', '已撤销结清'))}
          openPhoto={openPhoto}
        />
      )}

      {toast && (
        <div data-rcv-toast={toast.kind} style={{
          position: 'fixed', left: isMobile ? 10 : '50%', right: isMobile ? 10 : 'auto', bottom: isMobile ? 76 : 24,
          transform: isMobile ? 'none' : 'translateX(-50%)', zIndex: 3000, maxWidth: isMobile ? 'none' : 560,
          background: toast.kind === 'err' ? RED : '#18181B', color: '#fff', borderRadius: 10, padding: '11px 14px',
          fontSize: 14, fontWeight: 600, display: 'flex', gap: 12, alignItems: 'center', boxShadow: '0 6px 20px rgba(0,0,0,.25)',
        }}>
          <span style={{ flex: 1, whiteSpace: 'pre-line' }}>{toast.msg}</span>
          {toast.undo && <button data-rcv-undo onClick={toast.undo} style={{ background: 'none', border: '1px solid #fff', color: '#fff', borderRadius: 6, padding: '4px 10px', fontWeight: 700, cursor: 'pointer' }}>{B('되돌리기', '撤销')}</button>}
        </div>
      )}

      {photoView && (
        <div data-rcv-photo-modal onClick={() => setPhotoView(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', zIndex: 3100, display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'auto', padding: 12,
        }}>
          {photoView.loading ? <div style={{ color: '#fff', fontSize: 15 }}>{B('사진 불러오는 중…', '照片加载中…')}</div>
            : photoView.error ? <div style={{ color: '#fff', fontSize: 15, whiteSpace: 'pre-line', textAlign: 'center' }}>{photoView.error}</div>
              : <img src={photoView.data} alt="receipt" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', background: '#fff' }} />}
          <button onClick={() => setPhotoView(null)} style={{ position: 'fixed', top: 12, right: 12, background: '#fff', border: 'none', borderRadius: 999, width: 38, height: 38, fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>
      )}
    </div>
  );
}

// ── 📥 입고하기: 입고 대기 발주서 ─────────────────────────────────────────────
function NewPane({ orderHistory, receivings, prices, today, vendors, zhMap, Z, zh, money, vLabel, payText, U, onStart, onFree, onSkip, say, loading }) {
  const { B, P } = Z;
  const [more, setMore] = useState(false);
  const [sel, setSel] = useState([]);
  const [freeOpen, setFreeOpen] = useState(false);
  const [freeV, setFreeV] = useState('');
  const pending = useMemo(() => pendingOrders(orderHistory, today, more ? 31 : PENDING_DAYS), [orderHistory, today, more]);
  const olderCount = useMemo(() => (more ? 0 : pendingOrders(orderHistory, today, 31).length - pending.length), [orderHistory, today, more, pending.length]);
  const picked = pending.filter(h => sel.includes(h.id));
  const todays = receivings.filter(r => r.date === today);

  const toggle = (h) => {
    if (sel.includes(h.id)) { setSel(sel.filter(x => x !== h.id)); return; }
    if (picked.length && orderVendor(picked[0]) !== orderVendor(h)) {
      say(Z('같은 발주처 발주서끼리만 함께 입고할 수 있어요', '只能把同一供应商的订货单一起入库'), 'err');
      return;
    }
    setSel([...sel, h.id]);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 15, fontWeight: 800 }}>{B('입고 대기 발주서', '待入库的订货单')} <span style={{ color: '#9ca3af', fontWeight: 600 }}>{pending.length}</span></div>
        <button data-rcv-free onClick={() => setFreeOpen(o => !o)} style={btn(freeOpen, BLUE)}>＋ {B('발주서 없이 입고', '无订货单直接入库')}</button>
      </div>

      {freeOpen && (
        <div style={{ ...card, padding: 12, marginBottom: 12, background: '#F8FAFC' }} data-rcv-free-box>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{B('어느 발주처에서 받았나요?', '是哪个供应商送的货？')}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
            {vendors.map(v => (
              <button key={v.vendor} data-rcv-vendor-pick={v.vendor} onClick={() => onFree(v.vendor)} style={chip(false, BLUE)}>
                🏪 {vLabel(v.vendor, v.vendor_zh)}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input data-rcv-vendor-input value={freeV} onChange={e => setFreeV(e.target.value)} placeholder={P('목록에 없으면 직접 입력', '列表里没有就直接输入')}
              onKeyDown={e => { if (e.key === 'Enter') onFree(freeV); }}
              style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }} />
            <button data-rcv-vendor-ok onClick={() => onFree(freeV)} disabled={!freeV.trim()} style={btn(!!freeV.trim(), BLUE)}>{B('시작', '开始')}</button>
          </div>
        </div>
      )}

      {picked.length > 1 && (
        <div style={{ ...card, padding: '10px 12px', marginBottom: 10, background: '#FFF7ED', borderColor: ORANGE, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ flex: 1, fontSize: 14, fontWeight: 700 }}>{B(`발주서 ${picked.length}장 함께 — 같은 품목은 합쳐요`, `${picked.length} 张订货单一起 — 相同食材合并`)}</span>
          <button data-rcv-multi-start onClick={() => { onStart(picked); setSel([]); }} style={btn(true)}>📥 {B('함께 입고하기', '一起入库')}</button>
          <button onClick={() => setSel([])} style={btn(false)}>{B('선택 해제', '取消选择')}</button>
        </div>
      )}

      {loading && !pending.length ? (
        <div style={{ color: '#9ca3af', fontSize: 14, padding: 20, textAlign: 'center' }}>{B('불러오는 중…', '加载中…')}</div>
      ) : !pending.length ? (
        <div style={{ ...card, padding: '22px 16px', textAlign: 'center', color: '#6b7280', fontSize: 14, lineHeight: 1.6 }} data-rcv-empty>
          {B('입고 대기 중인 발주서가 없어요.', '没有待入库的订货单。')}<br />
          {B('발주서 없이 받은 물건은 위의 ＋ 버튼으로 바로 입력하세요.', '没有订货单的货，请点上面的 ＋ 直接录入。')}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 8 }}>
          {pending.map(h => {
            const v = orderVendor(h);
            const open = orderOpenItems(h);
            const isPart = h.receive?.state === 'partial';
            const on = sel.includes(h.id);
            return (
              <div key={h.id} data-rcv-order={h.id} style={{ ...card, padding: '11px 12px', borderColor: on ? ORANGE : '#e5e7eb', background: on ? '#FFF7ED' : '#fff', display: 'flex', gap: 10, alignItems: 'center' }}>
                <input type="checkbox" checked={on} onChange={() => toggle(h)} data-rcv-order-pick={h.id} style={{ width: 19, height: 19, accentColor: ORANGE, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }} onClick={() => toggle(h)}>
                  <div style={{ fontSize: 14, fontWeight: 800, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span>{dayLabel(h.date, zh)}</span>
                    <span style={{ color: '#1e40af' }}>🏪 {vLabel(v)}</span>
                    {isPart && <span style={{ fontSize: 11, fontWeight: 700, color: '#9A3412', background: '#FFEDD5', padding: '1px 6px', borderRadius: 4 }}>{B(`일부 입고 · ${open.length}품목 남음`, `部分入库 · 剩 ${open.length} 项`)}</span>}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {open.length}{B('품목', ' 项')} · {money(h.totalCost || 0)} · {open.slice(0, 4).map(i => `${nameBoth(zh, i.name, i.name_zh || zhOf(zhMap, i.name))} ${r3(i.openQty)}${U(i.unit)}`).join(zh ? '、' : ', ')}{open.length > 4 ? ' …' : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flexShrink: 0 }}>
                  <button data-rcv-start={h.id} onClick={() => onStart([h])} style={btn(true)}>📥 {B('입고', '入库')}</button>
                  <button data-rcv-skip={h.id} onClick={() => onSkip([h])} style={{ ...btn(false), fontSize: 11, padding: '4px 8px', color: '#6b7280' }}>{B('입고 안 함', '不入库')}</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {olderCount > 0 && (
        <button data-rcv-more onClick={() => setMore(true)} style={{ ...btn(false), marginTop: 10, width: '100%', color: '#6b7280' }}>
          {B(`지난 발주서 ${olderCount}장 더 보기 (${PENDING_DAYS}일 전 ~ 한 달)`, `再看 ${olderCount} 张以前的订货单（${PENDING_DAYS} 天前 ~ 一个月）`)}
        </button>
      )}

      {todays.length > 0 && (
        <div style={{ marginTop: 18 }} data-rcv-today>
          <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 6 }}>
            {B('오늘 입고', '今天入库')} <span style={{ color: '#9ca3af', fontWeight: 600 }}>{todays.length}{B('건', ' 笔')} · {money(todays.reduce((a, r) => a + (Number(r.total) || 0), 0))}</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 6 }}>
            {todays.map(r => (
              <div key={r.id} style={{ ...card, padding: '8px 12px', fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontWeight: 700, color: '#1e40af' }}>🏪 {vLabel(r.vendor, r.vendor_zh)}</span>
                <span style={{ color: '#6b7280' }}>{(r.items || []).length}{B('품목', ' 项')}</span>
                <span style={{ marginLeft: 'auto', fontWeight: 800 }}>{money(r.total)}</span>
                <PayBadge r={r} payText={payText} Z={Z} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PayBadge({ r, payText, Z }) {
  const { B } = Z;
  const unpaid = r.payment === 'credit' && !r.paid;
  const settled = r.payment === 'credit' && r.paid;
  return (
    <span data-rcv-pay-badge={unpaid ? 'unpaid' : r.payment} style={{
      fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 5, whiteSpace: 'nowrap',
      color: unpaid ? RED : settled ? GREEN : '#3f3f46', background: unpaid ? '#FEF2F2' : settled ? '#F0FDF4' : '#F4F4F5',
    }}>
      {unpaid ? <>📒 {B('외상', '赊账')}</> : settled ? <>✓ {B('외상 정산', '赊账已结')}</> : payText(r.payment)}
    </span>
  );
}

// ── 입고 입력 화면 ────────────────────────────────────────────────────────────
function Editor({ draft, setDraft, prices, receivings, packMode, vendors, zhMap, Z, zh, U, money, vLabel, payText, isMobile, compressPhoto, say, onSave, openPhoto, translator }) {
  const { B, P } = Z;
  const set = (patch) => setDraft(d => ({ ...d, ...patch }));
  // V161: 번역이 끝나기 전에 저장을 누르면 잠깐 기다렸다가 (최대 8초 — V162 번역이 안 되면 병음 한글까지) 저장
  const draftRef = useRef(draft); draftRef.current = draft;
  const pendingRef = useRef(new Set());
  const trDoneRef = useRef(new Map());   // 끝난 번역 { 줄 id: 고칠 내용 } — 화면이 다시 그려지기 전에 저장해도 빠지지 않게
  const [waitTr, setWaitTr] = useState(false);
  // V161 → V172: 새 품목 이름 번역 (한국어만 → 중국어, 중국어만 → 한국어). 여기 두고 AddItems가 부른다
  const trDone = (id, patch) => {
    trDoneRef.current.set(id, { ...(trDoneRef.current.get(id) || {}), ...patch });
    setDraft(d => (d ? { ...d, lines: d.lines.map(l => (l.id === id ? { ...l, ...patch } : l)) } : d));
  };
  const track = (pr) => { const st = pendingRef.current; st.add(pr); pr.finally(() => st.delete(pr)); };
  const startTr = (line) => {
    if (line.koPending && translator?.toKo) {
      const src = line.name_zh || line.name;
      track(Promise.resolve(translator.toKo(src)).then(ko => {
        const k = String(ko || '').trim();
        trDone(line.id, k && hasHangul(k) ? { name: k, okey: okeyOf(k, ''), koPending: false, retr: false } : { koPending: false, retr: false });
      }).catch(() => trDone(line.id, { koPending: false, retr: false })));
    }
    if (line.zhPending && translator?.toZh) {
      track(Promise.resolve(translator.toZh(line.name)).then(z => {
        const v = String(z || '').trim();
        trDone(line.id, v && hasCjk(v) ? { name_zh: v, zhPending: false, retr: false } : { zhPending: false, retr: false });
      }).catch(() => trDone(line.id, { zhPending: false, retr: false })));
    }
  };
  // V172: 언어를 바꿔 화면이 새로 그려졌을 때 — 번역 중이던 이름은 다시 번역 (앞의 번역 결과는 사라진 화면으로 갔다)
  useEffect(() => {
    for (const l of draftRef.current.lines || []) if (l.retr && (l.koPending || l.zhPending)) startTr(l);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const doSave = async () => {
    if (pendingRef.current.size) {
      setWaitTr(true);
      await Promise.race([Promise.allSettled([...pendingRef.current]), new Promise(r => setTimeout(r, 8000))]);
      await new Promise(r => setTimeout(r, 30));
      setWaitTr(false);
    }
    const d0 = draftRef.current;
    const done = trDoneRef.current;
    const d = done.size ? { ...d0, lines: d0.lines.map(l => (done.has(l.id) ? { ...l, ...done.get(l.id) } : l)) } : d0;
    onSave(d, priceUpdateCandidates(d.lines, prices, packMode));
  };
  // V161: 단가표 품목인데 중량(g)·단위가 빠져 있으면 알려 주고 여기서 채우게
  const rowByKey = useMemo(() => { const m = new Map(); for (const p of prices || []) m.set(orderKeyOf(p), p); return m; }, [prices]);
  const byCode = useMemo(() => rowsByCode(prices), [prices]);
  const gapItems = useMemo(() => {
    const seen = new Set(); const out = [];
    for (const l of draft.lines) {
      if (!l.key || seen.has(l.key) || !(lineQty(l) > 0)) continue;
      const row = rowByKey.get(l.key);
      // 단가는 이 줄에 적으면 '단가표에도 반영'으로 들어가니, 줄 단가도 비어 있을 때만 알린다
      const gaps = rowGaps(row).filter(g => g !== 'price' || !(linePrice(l) > 0));
      if (!gaps.length) continue;
      seen.add(l.key);
      out.push({ key: l.key, row, line: l, gaps });
    }
    return out;
  }, [draft.lines, rowByKey]);
  const setFix = (key, patch) => setDraft(d => ({ ...d, rowFix: { ...(d.rowFix || {}), [key]: { ...((d.rowFix || {})[key] || {}), ...patch } } }));
  const setLine = (id, patch) => setDraft(d => ({ ...d, lines: d.lines.map(l => (l.id === id ? { ...l, ...patch } : l)) }));
  const removeLine = (id) => setDraft(d => ({ ...d, lines: d.lines.filter(l => l.id !== id) }));
  const fileRef = useRef(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [vendorEdit, setVendorEdit] = useState(!draft.vendor);

  const total = sumLines(draft.lines);
  const rt = parseFloat(String(draft.receiptTotal || '').replace(',', '.'));
  const rtDiff = isFinite(rt) && rt > 0 ? Math.round((rt - total) * 100) / 100 : null;
  const cands = useMemo(() => priceUpdateCandidates(draft.lines, prices, packMode), [draft.lines, prices, packMode]);
  const ordered = draft.lines.some(l => Number(l.ordered) > 0);

  const pickPhoto = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    setPhotoBusy(true);
    try {
      const p = await compressPhoto(f);
      set({ photo: p, photoState: 'new' });
    } catch (err) {
      say(Z('사진이 너무 크거나 열 수 없어요', '照片太大或无法打开'), 'err');
    }
    setPhotoBusy(false);
  };

  const stepQty = (l, n) => setLine(l.id, { qty: String(Math.max(0, r3(lineQty(l) + n))) });

  return (
    <div data-rcv-editor style={{ paddingBottom: isMobile ? 70 : 0 }}>
      {/* 머리 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button data-rcv-back onClick={() => {
          if (draft.lines.some(l => lineQty(l) > 0) && !window.confirm(Z('입력한 내용을 저장하지 않고 나갈까요?', '不保存就离开吗？'))) return;
          setDraft(null);
        }} style={btn(false)}>← {B('뒤로', '返回')}</button>
        <div style={{ fontSize: 17, fontWeight: 800 }}>{draft.prev ? B('입고 고치기', '修改入库') : B('입고 입력', '录入入库')}</div>
        {draft.orderIds.length > 0 && <span style={{ fontSize: 12, color: '#6b7280' }}>{B(`발주서 ${draft.orderIds.length}장에서`, `来自 ${draft.orderIds.length} 张订货单`)}</span>}
      </div>

      <div style={{ ...card, padding: 12, marginBottom: 10, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#6b7280' }}>{B('발주처', '供应商')}</span>
        {vendorEdit ? (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1 }}>
            {vendors.slice(0, 12).map(v => (
              <button key={v.vendor} data-rcv-ed-vendor={v.vendor} onClick={() => { set({ vendor: v.vendor, vendor_zh: v.vendor_zh || '' }); setVendorEdit(false); }}
                style={chip(draft.vendor === v.vendor, BLUE)}>{vLabel(v.vendor, v.vendor_zh)}</button>
            ))}
          </div>
        ) : (
          <button onClick={() => setVendorEdit(true)} data-rcv-vendor={draft.vendor}
            style={{ background: 'none', border: 'none', fontSize: 16, fontWeight: 800, color: '#1e40af', cursor: 'pointer', padding: 0 }}>🏪 {vLabel(draft.vendor, draft.vendor_zh)} ✎</button>
        )}
        <span style={{ marginLeft: isMobile ? 0 : 'auto', fontSize: 13, fontWeight: 700, color: '#6b7280' }}>{B('입고일', '入库日期')}</span>
        <input type="date" data-rcv-date value={draft.date} onChange={e => set({ date: e.target.value })}
          style={{ padding: '6px 8px', borderRadius: 7, border: '1px solid #d1d5db', fontSize: 14 }} />
      </div>

      {/* 품목 */}
      <div style={{ ...card, overflow: 'hidden', marginBottom: 10 }}>
        <div style={{ padding: '9px 12px', background: '#F9FAFB', borderBottom: '1px solid #e5e7eb', fontSize: 13, fontWeight: 700, color: '#3f3f46', display: 'flex', gap: 8 }}>
          <span style={{ flex: 1 }}>{B('받은 품목', '实收食材')} · {draft.lines.filter(l => lineQty(l) > 0).length}</span>
          {ordered && <span style={{ color: '#9ca3af', fontWeight: 600 }}>{B('영수증 보고 받은 양·단가로 고쳐 주세요', '请对照送货单修改实收数量和单价')}</span>}
        </div>
        {draft.lines.length === 0 && (
          <div style={{ padding: 16, color: '#9ca3af', fontSize: 14, textAlign: 'center' }}>{B('아래 ＋ 품목 추가로 받은 품목을 넣어 주세요', '请用下面的 ＋ 添加食材')}</div>
        )}
        {draft.lines.map(l => {
          const q = lineQty(l);
          const none = Number(l.ordered) > 0 && q <= 0;
          const diff = priceDiff(l, prices, packMode);
          const changed = Number(l.ordered) > 0 && q > 0 && Math.abs(q - Number(l.ordered)) > 0.0005;
          return (
            <div key={l.id} data-rcv-line={l.name} style={{ padding: '10px 12px', borderBottom: '1px solid #f1f1f3', background: none ? '#FAFAFA' : '#fff' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 700, color: none ? '#9ca3af' : '#18181B', textDecoration: none ? 'line-through' : 'none' }}>
                  {(() => { const np = shownParts(l, byCode); return <BiName ko={np.ko} zh={np.zh} zhMap={zhMap} pron={false} />; })()}
                  {l.spec && <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500, marginLeft: 4 }}>{zh ? (specToZh(l.spec) || l.spec) : l.spec}</span>}
                  {(l.koPending || l.zhPending) && <span data-rcv-tr-pending style={{ display: 'block', fontSize: 11, color: '#9ca3af', fontWeight: 500 }}>{l.koPending ? B('한국어 이름 번역 중…', '正在翻译成韩语…') : B('중국어 이름 번역 중…', '正在翻译成中文…')}</span>}
                </div>
                {Number(l.ordered) > 0 && (
                  <span style={{ fontSize: 12, color: changed ? '#9A3412' : '#6b7280', fontWeight: 700, whiteSpace: 'nowrap', marginTop: 2 }}>
                    {B('주문', '订')} {r3(l.ordered)}{U(l.unit)}{changed ? ` → ${r3(q)}` : ''}
                  </span>
                )}
              </div>
              {none ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                  <span data-rcv-none-label style={{ fontSize: 12, fontWeight: 700, color: RED }}>{B('안 옴', '没送')}</span>
                  <button data-rcv-restore onClick={() => setLine(l.id, { qty: String(l.ordered) })} style={{ ...btn(false), fontSize: 12, padding: '4px 9px' }}>{B('받음으로', '改为已收')}</button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 7, flexWrap: 'wrap' }}>
                  <button data-rcv-dec onClick={() => stepQty(l, -1)} style={{ ...btn(false), padding: '5px 10px' }}>−</button>
                  <input data-rcv-qty inputMode="decimal" value={l.qty} onChange={e => setLine(l.id, { qty: e.target.value })} onFocus={e => e.target.select()} style={numIn} />
                  <button data-rcv-inc onClick={() => stepQty(l, 1)} style={{ ...btn(false), padding: '5px 10px' }}>＋</button>
                  <span style={{ fontSize: 13, color: '#3f3f46', minWidth: 18 }}>{U(l.unit)}</span>
                  <span style={{ fontSize: 13, color: '#9ca3af' }}>×</span>
                  <input data-rcv-price inputMode="decimal" value={l.price} onChange={e => setLine(l.id, { price: e.target.value })} onFocus={e => e.target.select()} style={{ ...numIn, width: 70 }} />
                  <span style={{ fontSize: 12, color: '#9ca3af' }}>/{U(l.unit)}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 15, fontWeight: 800 }}>{money(lineAmount(l))}</span>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5, flexWrap: 'wrap' }}>
                {!none && <DiffBadge diff={diff} line={l} Z={Z} U={U} money={money} />}
                {(() => {
                  const g = !none && gapItems.find(x => x.key === l.key);
                  if (!g) return null;
                  const fx = (draft.rowFix || {})[l.key] || {};
                  const left = g.gaps.filter(k => (k === 'net_g' ? !(Number(fx.net_g) > 0) : k === 'unit' ? !fx.unit : true));
                  if (!left.length) return null;
                  const nmK = { price: '단가', unit: '단위', net_g: '중량(g)' };
                  const nmZ = { price: '单价', unit: '单位', net_g: '重量(g)' };
                  return (
                    <span data-rcv-gap style={{ fontSize: 11, fontWeight: 700, color: '#B45309', background: '#FEF3C7', padding: '2px 7px', borderRadius: 5 }}>
                      ⚠ {B(`단가표에 ${left.map(k => nmK[k]).join('·')} 없음${left.includes('price') ? ' — 이 줄에 단가를 적어 주세요' : ' — 아래에서 채우기'}`,
                        `单价表缺${left.map(k => nmZ[k]).join('·')}${left.includes('price') ? ' — 请在这行填写单价' : ' — 请在下面补全'}`)}
                    </span>
                  );
                })()}
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                  <AcctSelect value={acctOfLine(l)} onChange={a => setLine(l.id, { acct: a })} zh={zh} />
                  {Number(l.ordered) > 0 && !none && <button data-rcv-none onClick={() => setLine(l.id, { qty: '0' })} style={{ ...btn(false), fontSize: 11, padding: '3px 8px', color: '#6b7280' }}>{B('안 옴', '没送')}</button>}
                  {!(Number(l.ordered) > 0) && <button data-rcv-remove onClick={() => removeLine(l.id)} style={{ ...btn(false), fontSize: 11, padding: '3px 8px', color: '#6b7280' }}>{B('빼기', '删除')}</button>}
                </span>
              </div>
            </div>
          );
        })}
        <AddItems draft={draft} setDraft={setDraft} prices={prices} receivings={receivings} packMode={packMode} Z={Z} zh={zh} U={U} money={money} zhMap={zhMap}
          say={say} startTr={startTr} />
      </div>

      {/* 합계 · 영수증 */}
      <div style={{ ...card, padding: 12, marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 15, fontWeight: 800 }}>{B('합계', '合计')}</span>
          <span data-rcv-total style={{ fontSize: 20, fontWeight: 900, color: ORANGE }}>{money(total)}</span>
          <span style={{ marginLeft: 'auto', fontSize: 13, color: '#6b7280', fontWeight: 700 }}>{B('영수증 합계', '送货单合计')}</span>
          <input data-rcv-receipt-total inputMode="decimal" value={draft.receiptTotal} onChange={e => set({ receiptTotal: e.target.value })}
            placeholder={P('선택', '可选')} style={{ ...numIn, width: 90 }} />
        </div>
        <AcctBreakdown amounts={acctAmounts({ items: draft.lines.filter(l => lineQty(l) > 0).map(l => ({ acct: acctOfLine(l), amount: lineAmount(l) })) })} money={money} zh={zh} onlyMixed />
        {rtDiff != null && (
          <div data-rcv-total-diff={Math.abs(rtDiff) < 0.01 ? 'ok' : 'diff'} style={{ marginTop: 6, fontSize: 13, fontWeight: 700, color: Math.abs(rtDiff) < 0.01 ? GREEN : RED }}>
            {Math.abs(rtDiff) < 0.01 ? B('✓ 영수증 합계와 같아요', '✓ 与送货单合计一致')
              : B(`영수증과 ${money(Math.abs(rtDiff))} ${rtDiff > 0 ? '모자라요 (빠진 품목?)' : '많아요 (수량·단가 확인)'}`,
                `与送货单相差 ${money(Math.abs(rtDiff))}${rtDiff > 0 ? '（漏了食材？）' : '（请核对数量和单价）'}`)}
          </div>
        )}
      </div>

      {/* 결제 */}
      <div style={{ ...card, padding: 12, marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#6b7280', marginBottom: 8 }}>{B('결제', '付款方式')}</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {PAY_METHODS.map(m => (
            <button key={m} data-rcv-pay={m} onClick={() => set({ payment: m })} style={chip(draft.payment === m, m === 'credit' ? RED : ORANGE)}>{payText(m)}</button>
          ))}
        </div>
        {draft.payment === 'credit' && <div style={{ fontSize: 12, color: RED, marginTop: 6 }}>{B('외상 잔액에 들어가요. 나중에 📒 외상 탭에서 정산하세요', '会计入赊账余额，以后在 📒 赊账 页结清')}</div>}
      </div>

      {/* 사진 · 메모 */}
      <div style={{ ...card, padding: 12, marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#6b7280' }}>{B('영수증 사진', '送货单照片')}</span>
          <input ref={fileRef} type="file" accept="image/*" data-rcv-photo-input onChange={pickPhoto} style={{ display: 'none' }} />
          {draft.photoState === 'new' && draft.photo ? (
            <>
              <img src={draft.photo.data} alt="" data-rcv-photo-thumb onClick={() => openPhoto('new', draft.photo.data)}
                style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb', cursor: 'pointer' }} />
              <button onClick={() => fileRef.current && fileRef.current.click()} style={btn(false)}>{B('바꾸기', '更换')}</button>
              <button data-rcv-photo-remove onClick={() => set({ photo: null, photoState: draft.prev?.hasPhoto ? 'removed' : 'none' })} style={btn(false)}>{B('빼기', '删除')}</button>
            </>
          ) : draft.photoState === 'keep' ? (
            <>
              <button data-rcv-photo-view onClick={() => openPhoto(draft.id)} style={btn(false, BLUE)}>📷 {B('저장된 사진 보기', '查看已存照片')}</button>
              <button onClick={() => fileRef.current && fileRef.current.click()} style={btn(false)}>{B('바꾸기', '更换')}</button>
              <button data-rcv-photo-remove onClick={() => set({ photoState: 'removed' })} style={btn(false)}>{B('빼기', '删除')}</button>
            </>
          ) : (
            <button data-rcv-photo-add onClick={() => fileRef.current && fileRef.current.click()} disabled={photoBusy} style={btn(false, BLUE)}>
              {photoBusy ? B('사진 줄이는 중…', '照片处理中…') : <>📷 {B('사진 찍기 · 고르기', '拍照 · 选照片')}</>}
            </button>
          )}
          {draft.photoState === 'removed' && <span style={{ fontSize: 12, color: RED }}>{B('저장하면 사진이 지워져요', '保存后照片会删除')}</span>}
        </div>
        <textarea data-rcv-memo value={draft.memo} maxLength={MEMO_MAX} onChange={e => set({ memo: e.target.value })} rows={2}
          placeholder={P('메모 (선택) — 예: 청피망 상태 안 좋음, 내일 2근 더 옴', '备注（可选）— 例：青椒不新鲜，明天再送 2 斤')}
          style={{ width: '100%', boxSizing: 'border-box', marginTop: 10, padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, fontFamily: 'inherit', resize: 'vertical' }} />
      </div>

      {/* V161: 단가표에 빠진 정보 */}
      {gapItems.length > 0 && (
        <div style={{ ...card, padding: 12, marginBottom: 10, background: '#FFFBEB', borderColor: '#F59E0B' }} data-rcv-gap-box>
          <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 4 }}>⚠ {B(`단가표에 빠진 정보 — ${gapItems.length}개 품목`, `单价表缺少信息 — ${gapItems.length} 项`)}</div>
          <div style={{ fontSize: 12, color: '#92400E', marginBottom: 8 }}>{B('단가·단위·중량(g)이 있어야 레시피 원가가 계산돼요. 여기 적으면 저장할 때 단가표에 같이 들어가요.', '有单价·单位·重量(g)才能计算配方成本。在这里填写，保存时会一起写入单价表。')}</div>
          {gapItems.map(({ key, row, line, gaps }) => {
            const fx = (draft.rowFix || {})[key] || {};
            const sg = suggestNetG({ ...row, unit: fx.unit || row.unit });
            const np = nameParts(row);
            const specTxt = row.spec ? (zh ? (specToZh(row.spec) || row.spec) : row.spec) : '';
            return (
              <div key={key} data-rcv-gap-row={row.code || row.name} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', padding: '6px 0', borderTop: '1px solid #FDE68A' }}>
                <span style={{ fontWeight: 700, fontSize: 13, minWidth: 90 }}><NameBi np={np} zh={zh} />{specTxt ? <span style={{ color: '#9ca3af', fontWeight: 500 }}> {specTxt}</span> : null}</span>
                {gaps.includes('price') && (
                  <span data-rcv-fix-price style={{ fontSize: 12, color: '#B45309' }}>{B('단가 없음 → 위 줄에 단가를 적으면 단가표에도 들어가요', '没有单价 → 在上面这行填写单价后会写入单价表')}</span>
                )}
                {gaps.includes('unit') && (
                  <select data-rcv-fix-unit value={fx.unit || ''} onChange={e => setFix(key, { unit: e.target.value })} style={{ padding: '6px', borderRadius: 7, border: '1px solid #d1d5db', fontSize: 13 }}>
                    <option value="">{P('단위', '单位')}</option>
                    {['근', '개', 'kg', 'g', '봉', '병', '판', '박스', '통', '팩', '캔'].map(u => <option key={u} value={u}>{P(u, unitToZh(u) || u)}</option>)}
                  </select>
                )}
                {gaps.includes('net_g') && (
                  <>
                    <input data-rcv-fix-netg inputMode="decimal" value={fx.net_g || ''} onChange={e => setFix(key, { net_g: e.target.value })}
                      placeholder={P(`중량 g (${specTxt || '1' + (row.unit || '')} 기준)`, `重量 g（按 ${specTxt || '1' + U(row.unit || '')}）`)} style={{ ...numIn, width: 190, textAlign: 'left' }} />
                    {sg > 0 && !(Number(fx.net_g) > 0) && <button data-rcv-fix-suggest onClick={() => setFix(key, { net_g: String(sg) })} style={{ ...btn(false), fontSize: 12, padding: '4px 9px' }}>{sg}g</button>}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* 단가표 반영 */}
      {cands.length > 0 && (
        <div style={{ ...card, padding: 12, marginBottom: 10, background: '#FFFBEB', borderColor: '#FCD34D' }} data-rcv-pu-box>
          <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 6 }}>{B('단가표에도 반영', '同时更新单价表')} <span style={{ fontSize: 12, color: '#92400E', fontWeight: 600 }}>{B('(체크한 것만)', '（只更新勾选的）')}</span></div>
          {draft.prev && <div style={{ fontSize: 12, color: '#92400E', marginBottom: 4 }}>{B('고칠 때는 꺼진 채로 시작해요 — 단가표를 바꾸려면 체크하세요', '修改时默认不更新 — 需要更新单价表请勾选')}</div>}
          {cands.map(({ line, diff }) => {
            const on = puOn(draft, line.id);
            const info = diff.row ? orderPackInfo(diff.row, packMode) : { pack: 1, perPack: false };
            const packed = info.pack > 1 && info.perPack;
            return (
              <label key={line.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '5px 0', fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" data-rcv-pu={line.name} checked={on} style={{ width: 17, height: 17, accentColor: ORANGE }}
                  onChange={() => set(puToggle(draft, line.id, on))} />
                <span style={{ fontWeight: 700 }}><NameBi np={nameParts(line)} zh={zh} /></span>
                <span style={{ color: '#6b7280' }}>
                  {diff.state === 'new' ? (() => {
                    const same = findSameNameRow(prices, nameParts(line), draft.vendor);
                    if (same) { const sp = nameParts(same); return B(`단가표의 '${sp.ko}'에 잇기${Number(same.price) > 0 ? '' : ` · ${money(linePrice(line))}/${U(line.unit)}`}`, `关联单价表里的「${sp.zh || sp.ko}」${Number(same.price) > 0 ? '' : ` · ${money(linePrice(line))}/${U(line.unit)}`}`); }
                    return B(`단가표에 새로 넣기 · ${money(linePrice(line))}/${U(line.unit)}`, `新加入单价表 · ${money(linePrice(line))}/${U(line.unit)}`);
                  })()
                    : diff.state === 'empty' ? B(`비어 있던 단가 → ${money(linePrice(line))}/${U(line.unit)}`, `原来没有单价 → ${money(linePrice(line))}/${U(line.unit)}`)
                      : `${money(diff.base)} → ${money(linePrice(line))}/${U(line.unit)}${packed ? ` (${diff.row.spec} ${money(Number(diff.row.price) || 0)} → ${money(Math.round(linePrice(line) * info.pack * 100) / 100)})` : ''}`}
                </span>
              </label>
            );
          })}
        </div>
      )}

      {/* 저장 */}
      <div style={isMobile ? { position: 'fixed', left: 0, right: 0, bottom: 56, padding: '8px 10px', background: 'rgba(255,255,255,.96)', borderTop: '1px solid #e5e7eb', zIndex: 900 } : { marginTop: 4 }}>
        <button data-rcv-save onClick={doSave} disabled={waitTr} style={{ ...btn(true), width: '100%', padding: '13px 14px', fontSize: 16, opacity: waitTr ? 0.7 : 1 }}>
          {waitTr ? B('번역 기다리는 중…', '等待翻译…') : <>💾 {draft.prev ? B('고친 내용 저장', '保存修改') : B('입고 저장', '保存入库')} · {money(total)}{draft.payment === 'credit' ? B(' (외상)', '（赊账）') : ''}</>}
        </button>
      </div>
    </div>
  );
}

function DiffBadge({ diff, line, Z, U, money }) {
  if (diff.state === 'same') return null;
  const { B } = Z;
  const pct = Math.round(Math.abs(diff.ratio) * 100);
  const map = {
    up: { c: RED, bg: '#FEF2F2', t: B(`▲ ${pct}% 비쌈 · 단가표 ${money(diff.base)}/${U(line.unit)}`, `▲ 贵 ${pct}% · 单价表 ${money(diff.base)}/${U(line.unit)}`) },
    down: { c: '#1D4ED8', bg: '#EFF6FF', t: B(`▼ ${pct}% 쌈 · 단가표 ${money(diff.base)}/${U(line.unit)}`, `▼ 便宜 ${pct}% · 单价表 ${money(diff.base)}/${U(line.unit)}`) },
    empty: { c: '#6b7280', bg: '#F4F4F5', t: B('단가표에 단가 없음', '单价表里没有单价') },
    new: { c: '#7C3AED', bg: '#F5F3FF', t: B('단가표에 없는 품목', '单价表里没有这个食材') },
  }[diff.state];
  if (!map) return null;
  return <span data-rcv-diff={diff.state} style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, color: map.c, background: map.bg, padding: '2px 7px', borderRadius: 5 }}>{map.t}</span>;
}

// ── 품목 추가 (그 발주처 단가표 · 새 품목) ────────────────────────────────────
function AddItems({ draft, setDraft, prices, receivings, packMode, Z, zh, U, money, zhMap, say, startTr }) {
  const { B, P } = Z;
  const [open, setOpen] = useState(!!draft.addOpen);
  const [q, setQ] = useState('');
  const [allV, setAllV] = useState(false);
  const [nw, setNw] = useState(null);   // { name, unit, price }
  const inLines = new Set(draft.lines.map(l => l.key).filter(Boolean));

  const freq = useMemo(() => {
    const m = new Map();
    for (const r of receivings || []) {
      if (r.vendor !== draft.vendor) continue;
      for (const it of r.items || []) if (it.key) m.set(it.key, (m.get(it.key) || 0) + 1);
    }
    return m;
  }, [receivings, draft.vendor]);

  const list = useMemo(() => {
    const qs = q.trim() ? [...new Set(searchVariants(q.trim()).map(v => v.toLowerCase()).filter(Boolean))] : [];
    return (prices || [])
      .filter(p => p && p.name && p.category !== '반제품' && !p.hidden)
      .filter(p => allV || qs.length ? true : String(p.vendor || '').trim() === draft.vendor)
      .filter(p => !qs.length || qs.some(v => String(p.name).toLowerCase().includes(v) || String(p.name_zh || '').includes(v) || String(p.code || '').toLowerCase() === v))
      .map(p => ({ p, k: orderKeyOf(p), own: String(p.vendor || '').trim() === draft.vendor }))
      .sort((a, b) => (b.own - a.own) || ((freq.get(b.k) || 0) - (freq.get(a.k) || 0)) || String(a.p.name).localeCompare(String(b.p.name)))
      .slice(0, 60);
  }, [prices, q, allV, draft.vendor, freq]);

  const add = (p) => setDraft(d => ({ ...d, lines: [...d.lines, lineFromRow(p, packMode, '1')] }));
  // V161: 중국어로만 적어도 된다 — 한국어는 자동 번역. 단가표에 같은 이름(한국어·중국어)이 있으면 그 품목으로
  const addNew = () => {
    if (!nw || !nw.name.trim()) return;
    const p = parseFloat(String(nw.price || '').replace(',', '.'));
    const sp = splitKoZh(nw.name);
    const same = findSameNameRow(prices, sp, draft.vendor);
    if (same) {
      const l = lineFromRow(same, packMode, '1');
      if (isFinite(p) && p > 0) l.price = p;
      setDraft(d => ({ ...d, lines: [...d.lines, l] }));
      setNw(null);
      const np = nameParts(same);
      say && say(Z(`단가표에 있는 '${np.ko}'(으)로 넣었어요`, `已按单价表里的「${np.zh || np.ko}」添加`));
      return;
    }
    const line = newItemLine({ name: sp.ko || sp.zh, name_zh: sp.zh, unit: nw.unit || '개', price: isFinite(p) ? p : 0, qty: '1', acct: nw.acct, net_g: nw.net_g });
    if (!sp.ko && sp.zh) line.koPending = true;
    if (sp.ko && !sp.zh) line.zhPending = true;
    setDraft(d => ({ ...d, lines: [...d.lines, line] }));
    setNw(null);
    if (startTr) startTr(line);
  };

  if (!open) {
    return (
      <div style={{ padding: 10 }}>
        <button data-rcv-add-open onClick={() => setOpen(true)} style={{ ...btn(false, BLUE), width: '100%', borderStyle: 'dashed' }}>＋ {B('품목 추가 (주문 안 했는데 온 것)', '添加食材（没订但送来的）')}</button>
      </div>
    );
  }
  return (
    <div style={{ padding: 10, background: '#F8FAFC' }} data-rcv-add-box>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
        <input data-rcv-add-q value={q} onChange={e => setQ(e.target.value)} placeholder={P('품목 찾기 (이름 · 중국어 · 코드)', '搜索食材（名称 · 中文 · 编号）')}
          style={{ flex: 1, minWidth: 160, padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }} />
        <button onClick={() => setAllV(v => !v)} style={chip(allV, BLUE)}>{B('다른 발주처 품목도', '其他供应商的也显示')}</button>
        <button onClick={() => setOpen(false)} style={btn(false)}>{B('닫기', '收起')}</button>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', maxHeight: 220, overflowY: 'auto' }}>
        {list.map(({ p, k, own }) => {
          const has = inLines.has(k);
          return (
            <button key={k} data-rcv-add-row={p.code || p.name} onClick={() => add(p)}
              style={{ ...chip(false, BLUE), borderColor: has ? GREEN : '#d1d5db', background: has ? '#F0FDF4' : '#fff', textAlign: 'left' }}>
              {has ? '✓ ' : ''}<NameBi np={nameParts(p)} zh={zh} />
              <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500, marginLeft: 4 }}>
                {Number(p.price) > 0 ? money(Number(p.price)) : ''}{p.spec ? ` ${p.spec}` : ''}{!own ? ` · ${p.vendor || '-'}` : ''}
              </span>
            </button>
          );
        })}
        {!list.length && <span style={{ fontSize: 13, color: '#9ca3af' }}>{B('단가표에서 못 찾았어요 — 아래에서 새 품목으로 넣으세요', '单价表里没找到 — 请在下面新加')}</span>}
      </div>
      {nw ? (
        <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }} data-rcv-new-box>
          <input data-rcv-new-name value={nw.name} onChange={e => setNw({ ...nw, name: e.target.value })} placeholder={P('품목 이름 (한국어나 중국어)', '食材名称（中文或韩语都行）')}
            style={{ flex: 1, minWidth: 120, padding: '7px 9px', borderRadius: 7, border: '1px solid #d1d5db', fontSize: 14 }} />
          <select data-rcv-new-unit value={nw.unit} onChange={e => setNw({ ...nw, unit: e.target.value })} style={{ padding: '7px 6px', borderRadius: 7, border: '1px solid #d1d5db', fontSize: 14 }}>
            {['근', '개', 'kg', '봉', '병', '판', '박스', '통', '단', '팩', '캔'].map(u => <option key={u} value={u}>{P(u, unitToZh(u) || u)}</option>)}
          </select>
          <input data-rcv-new-price inputMode="decimal" value={nw.price} onChange={e => setNw({ ...nw, price: e.target.value })} placeholder={P('단가', '单价')} style={{ ...numIn, width: 84 }} />
          {['근', 'kg'].includes(nw.unit) ? null : (
            <input data-rcv-new-netg inputMode="decimal" value={nw.net_g || ''} onChange={e => setNw({ ...nw, net_g: e.target.value })} placeholder={P(`1${nw.unit} 중량 g`, `每${U(nw.unit)}重量 g`)} style={{ ...numIn, width: 150 }} />
          )}
          <AcctSelect value={nw.acct || 'food'} onChange={a => setNw({ ...nw, acct: a })} zh={zh} dataAttr="data-rcv-new-acct" />
          <button data-rcv-new-add onClick={addNew} disabled={!nw.name.trim()} style={btn(!!nw.name.trim(), BLUE)}>{B('넣기', '添加')}</button>
          <button onClick={() => setNw(null)} style={btn(false)}>✕</button>
        </div>
      ) : (
        <button data-rcv-new-open onClick={() => setNw({ name: q.trim(), unit: '근', price: '', acct: lastNewAcct(draft) })} style={{ ...btn(false), marginTop: 10, fontSize: 12 }}>＋ {B('단가표에 없는 새 품목', '单价表里没有的新食材')}</button>
      )}
    </div>
  );
}

// ── 📚 입고 내역 ──────────────────────────────────────────────────────────────
// V161: 메모 — 보는 언어로 보여 주고 원문은 작게. 번역이 없으면 펼칠 때 한 번 번역해서 저장해 둔다
export function MemoView({ rec, zh, Z, translator, onPatch }) {
  const memo = String(rec?.memo || '');
  const memoHasKo = hasHangul(memo);
  const memoHasZh = hasCjk(memo);
  // 보는 언어와 메모 언어가 같으면 번역이 필요 없다
  const needTo = zh ? (memoHasKo ? 'zh' : null) : (memoHasZh && !memoHasKo ? 'ko' : null);
  const stored = needTo === 'zh' ? rec.memo_zh : needTo === 'ko' ? rec.memo_ko : null;
  const [tr, setTr] = useState(stored || '');
  const [busy, setBusy] = useState(false);
  const asked = useRef(false);
  useEffect(() => { if (stored) setTr(stored); }, [stored]);
  useEffect(() => {
    if (!needTo || stored || asked.current || !translator?.memo) return;
    asked.current = true;
    setBusy(true);
    Promise.resolve(translator.memo(memo))
      .then(r => {
        if (r && r.text && r.to === needTo) {
          setTr(r.text);
          if (onPatch && rec.id) Promise.resolve(onPatch(rec.id, needTo === 'zh' ? { memo_zh: r.text } : { memo_ko: r.text })).catch(() => {});
        }
      })
      .catch(() => {})
      .finally(() => setBusy(false));
  }, [needTo, stored, memo, translator, onPatch, rec.id]);
  const box = { fontSize: 13, color: '#3f3f46', marginTop: 6, whiteSpace: 'pre-wrap' };
  if (!needTo) return <div data-rcv-memo-view style={box}>📝 {memo}</div>;
  return (
    <div data-rcv-memo-view style={box}>
      📝 {tr || memo}
      {tr ? <span data-rcv-memo-orig style={{ display: 'block', fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{memo}</span>
        : busy ? <span style={{ display: 'block', fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{Z.B('번역 중…', '翻译中…')}</span> : null}
    </div>
  );
}

function HistoryPane({ receivings, today, Z, zh, U, money, vLabel, payText, payOne, zhMap, canDelete, onEdit, onDelete, openPhoto, loading, translator, onPatch, prices, orderHistory }) {
  const { B, P } = Z;
  const byCode = useMemo(() => rowsByCode(prices), [prices]);
  const [month, setMonth] = useState(String(today).slice(0, 7));
  const [vf, setVf] = useState('all');
  const [open, setOpen] = useState(null);
  const [q, setQ] = useState('');   // V163 품목 검색
  const cardProps = { byCode, Z, zh, U, money, vLabel, payText, payOne, zhMap, canDelete, onEdit, onDelete, openPhoto, translator, onPatch };
  const sum = useMemo(() => monthSummary(receivings, month), [receivings, month]);
  const list = useMemo(() => receivings
    .filter(r => String(r.date || '').startsWith(month) && (vf === 'all' || r.vendor === vf))
    .sort((a, b) => String(b.date).localeCompare(String(a.date)) || (Number(b.createdAt) || 0) - (Number(a.createdAt) || 0)), [receivings, month, vf]);
  const byDay = useMemo(() => {
    const m = new Map();
    for (const r of list) { if (!m.has(r.date)) m.set(r.date, []); m.get(r.date).push(r); }
    return [...m.entries()];
  }, [list]);
  const vfTotal = vf === 'all' ? sum.total : (sum.byVendor.find(v => v.vendor === vf)?.total || 0);

  const searchBox = (
    <div style={{ position: 'relative', marginBottom: 10 }}>
      <input data-rcv-hist-q value={q} onChange={e => { setQ(e.target.value); setOpen(null); }}
        placeholder={`🔍 ${P('품목으로 찾기 (모든 달)', '按食材搜索（全部月份）')}`}
        style={{ width: '100%', boxSizing: 'border-box', padding: '10px 36px 10px 12px', borderRadius: 10, border: `1.5px solid ${q.trim() ? ORANGE : '#e5e7eb'}`, fontSize: 14, background: '#fff' }} />
      {q && <button data-rcv-hist-q-clear onClick={() => { setQ(''); setOpen(null); }} style={{ position: 'absolute', right: 6, top: 6, border: 'none', background: 'none', fontSize: 16, color: '#9ca3af', cursor: 'pointer', padding: '2px 6px' }}>✕</button>}
    </div>
  );
  if (q.trim()) {
    return (
      <div>
        {searchBox}
        <SearchResults q={q.trim()} receivings={receivings} orderHistory={orderHistory} prices={prices} open={open} setOpen={setOpen} cardProps={cardProps} />
      </div>
    );
  }

  return (
    <div>
      {searchBox}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <button data-rcv-month-prev onClick={() => setMonth(m => monthShift(m, -1))} style={btn(false)}>◀</button>
        <div data-rcv-month style={{ fontSize: 16, fontWeight: 800, minWidth: 90, textAlign: 'center' }}>{zh ? `${month.slice(0, 4)}年${Number(month.slice(5))}月` : `${month.slice(0, 4)}년 ${Number(month.slice(5))}월`}</div>
        <button data-rcv-month-next onClick={() => setMonth(m => monthShift(m, 1))} disabled={month >= String(today).slice(0, 7)} style={{ ...btn(false), opacity: month >= String(today).slice(0, 7) ? 0.4 : 1 }}>▶</button>
        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          <div style={{ fontSize: 12, color: '#6b7280' }}>{vf === 'all' ? B('이달 입고 합계', '本月入库合计') : vLabel(vf)}</div>
          <div data-rcv-month-total style={{ fontSize: 19, fontWeight: 900, color: ORANGE }}>{money(vfTotal)}</div>
        </div>
      </div>
      {sum.byVendor.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          <button data-rcv-hv="all" onClick={() => setVf('all')} style={chip(vf === 'all', BLUE)}>{B('전체', '全部')} {sum.count}</button>
          {sum.byVendor.map(v => (
            <button key={v.vendor} data-rcv-hv={v.vendor} onClick={() => setVf(v.vendor)} style={chip(vf === v.vendor, BLUE)}>
              {vLabel(v.vendor, v.vendor_zh)} · {money(v.total)}{v.unpaid > 0 ? ' 📒' : ''}
            </button>
          ))}
        </div>
      )}
      {list.length > 0 && <div style={{ marginBottom: 10 }}><AcctBreakdown amounts={list.reduce((acc, r) => { for (const [a, v] of Object.entries(acctAmounts(r))) acc[a] = Math.round(((acc[a] || 0) + v) * 100) / 100; return acc; }, {})} money={money} zh={zh} /></div>}
      {loading && !list.length ? <div style={{ color: '#9ca3af', padding: 20, textAlign: 'center' }}>{B('불러오는 중…', '加载中…')}</div>
        : !list.length ? <div style={{ ...card, padding: 22, textAlign: 'center', color: '#9ca3af', fontSize: 14 }} data-rcv-history-empty>{B('이 달 입고 기록이 없어요', '本月没有入库记录')}</div>
          : byDay.map(([day, recs]) => (
            <div key={day} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#3f3f46', margin: '0 2px 6px', display: 'flex' }}>
                <span>{dayLabel(day, zh)}</span>
                <span style={{ marginLeft: 'auto', color: '#6b7280' }}>{money(recs.reduce((a, r) => a + (Number(r.total) || 0), 0))}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 6 }}>
                {recs.map(r => (
                  <EntryCard key={r.id} r={r} isOpen={open === r.id} onToggle={() => setOpen(open === r.id ? null : r.id)} {...cardProps} />
                ))}
              </div>
            </div>
          ))}
    </div>
  );
}

// V163: 입고 한 장 (달별 목록·검색 결과가 같이 쓴다). hl = 검색에 걸린 줄 번호 — 접혀 있어도 그 줄을 보여 준다
function EntryCard({ r, isOpen, onToggle, hl, byCode, Z, zh, U, money, vLabel, payText, payOne, zhMap, canDelete, onEdit, onDelete, openPhoto, translator, onPatch, showDate }) {
  const { B } = Z;
                  const rtDiff = r.receiptTotal ? Math.round((r.receiptTotal - r.total) * 100) / 100 : null;
                  return (
                    <div key={r.id} data-rcv-entry={r.id} style={{ ...card, overflow: 'hidden', borderColor: isOpen ? ORANGE : '#e5e7eb' }}>
                      <div onClick={onToggle} style={{ padding: '10px 12px', display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', flexWrap: 'wrap' }}>
                        {showDate && <span data-rcv-entry-date style={{ fontSize: 13, fontWeight: 800, color: '#3f3f46' }}>{dayLabel(r.date, zh)}</span>}
                        <span style={{ fontWeight: 800, color: '#1e40af', fontSize: 14 }}>🏪 {vLabel(r.vendor, r.vendor_zh)}</span>
                        <span style={{ fontSize: 12, color: '#6b7280' }}>{(r.items || []).length}{B('품목', ' 项')}{r.missing?.length ? B(` · 안 옴 ${r.missing.length}`, ` · 没送 ${r.missing.length}`) : ''}{r.hasPhoto ? ' · 📷' : ''}</span>
                        {Object.keys(acctAmounts(r)).filter(a => a !== 'food').map(a => <AcctBadge key={a} acct={a} zh={zh} />)}
                        <span style={{ marginLeft: 'auto', fontSize: 15, fontWeight: 800 }}>{money(r.total)}</span>
                        <PayBadge r={r} payText={payText} Z={Z} />
                      </div>
                      {!isOpen && hl && hl.length > 0 && (
                        <div data-rcv-hit-lines style={{ borderTop: '1px dashed #FED7AA', padding: '6px 12px 8px', background: '#FFFBF5' }}>
                          {hl.map(i => { const it = r.items[i]; if (!it) return null; const np = shownParts(it, byCode); return (
                            <div key={i} data-rcv-hit-line style={{ display: 'flex', gap: 8, fontSize: 13, padding: '2px 0' }}>
                              <span style={{ fontWeight: 700, flex: 1, minWidth: 0 }}>{zh ? (np.zh || np.ko) : np.ko}{(zh ? np.ko : np.zh) && <span style={{ color: '#9ca3af', fontWeight: 500, marginLeft: 4, fontSize: 12 }}>{zh ? np.ko : np.zh}</span>}</span>
                              <span style={{ whiteSpace: 'nowrap' }}>{r3(it.qty)}{U(it.unit)} × {money(it.price)}</span>
                              <span style={{ whiteSpace: 'nowrap', fontWeight: 700 }}>{money(it.amount)}</span>
                            </div>
                          ); })}
                        </div>
                      )}
                      {isOpen && (
                        <div style={{ borderTop: '1px solid #f1f1f3', padding: '8px 12px 12px' }} data-rcv-entry-detail>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <tbody>
                              {(r.items || []).map((it, i) => (
                                <tr key={i} data-rcv-hit={hl && hl.includes(i) ? '1' : undefined} style={{ borderBottom: '1px solid #f4f4f5', background: hl && hl.includes(i) ? '#FFF7ED' : undefined }}>
                                  <td style={{ padding: '6px 4px', fontWeight: 600 }}>{(() => { const np = shownParts(it, byCode); return <BiName ko={np.ko} zh={np.zh} zhMap={zhMap} pron={false} size={10} />; })()}{acctOfLine(it) !== 'food' && <span style={{ display: 'block', marginTop: 2 }}><AcctBadge acct={acctOfLine(it)} zh={zh} /></span>}</td>
                                  <td style={{ padding: '6px 4px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                    {r3(it.qty)}{U(it.unit)}
                                    {it.ordered > 0 && Math.abs(it.ordered - it.qty) > 0.0005 && <span style={{ display: 'block', fontSize: 10, color: '#9A3412' }}>{B('주문', '订')} {r3(it.ordered)}</span>}
                                  </td>
                                  <td style={{ padding: '6px 4px', textAlign: 'right', color: '#6b7280', whiteSpace: 'nowrap' }}>× {money(it.price)}</td>
                                  <td style={{ padding: '6px 4px', textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>{money(it.amount)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {r.missing?.length > 0 && <div style={{ fontSize: 12, color: RED, marginTop: 6 }}>{B('안 옴', '没送')}: {r.missing.map(m => `${nameBoth(zh, m.name, m.name_zh || zhOf(zhMap, m.name))} ${r3(m.ordered)}${U(m.unit)}`).join(zh ? '、' : ', ')}</div>}
                          {rtDiff != null && Math.abs(rtDiff) >= 0.01 && <div style={{ fontSize: 12, color: RED, marginTop: 4 }}>{B(`영수증 합계 ${money(r.receiptTotal)} (차이 ${money(rtDiff)})`, `送货单合计 ${money(r.receiptTotal)}（相差 ${money(rtDiff)}）`)}</div>}
                          {r.payment === 'credit' && r.paid && <div style={{ fontSize: 12, color: GREEN, marginTop: 4 }}>{B(`정산 ${r.paidAt || ''} · ${payOne(r.paidMethod, 'ko')}`, `已结清 ${r.paidAt || ''} · ${payOne(r.paidMethod, 'zh')}`)}</div>}
                          {r.memo && <MemoView rec={r} zh={zh} Z={Z} translator={translator} onPatch={onPatch} />}
                          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>
                            {r.createdBy || ''} {hhmm(r.createdAt)}{r.updatedAt ? B(` · 고침 ${r.updatedBy || ''} ${hhmm(r.updatedAt)}`, ` · 修改 ${r.updatedBy || ''} ${hhmm(r.updatedAt)}`) : ''}
                          </div>
                          <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                            {r.hasPhoto && <button data-rcv-photo-view onClick={() => openPhoto(r.id)} style={btn(false, BLUE)}>📷 {B('사진', '照片')}</button>}
                            <button data-rcv-edit onClick={() => onEdit(r)} style={btn(false)}>✎ {B('고치기', '修改')}</button>
                            {canDelete && <button data-rcv-delete onClick={() => onDelete(r)} style={{ ...btn(false), color: RED }}>🗑 {B('지우기', '删除')}</button>}
                          </div>
                        </div>
                      )}
                    </div>
  );
}

// V163: 품목 검색 결과 — 품목별 요약(몇 번 · 얼마나 · 마지막) + 발주 내역 + 걸린 입고 기록(모든 달, 최근 순)
function SearchResults({ q, receivings, orderHistory, prices, open, setOpen, cardProps }) {
  const { byCode, Z, zh, U, money, vLabel } = cardProps;
  const { B } = Z;
  const byName = useMemo(() => { const m = new Map(); for (const p of prices || []) if (p && p.name && !m.has(nameKey(p.name))) m.set(nameKey(p.name), p); return m; }, [prices]);
  const res = useMemo(() => searchReceivings(receivings, q, { rowOf: (it) => byCode.get(String(it.code || '').trim() || String(it.key || '').split('::')[0]) || byName.get(nameKey(it.name)) }), [receivings, q, byCode, byName]);
  const orders = useMemo(() => searchOrders(orderHistory, q, prices), [orderHistory, q, prices]);
  const rows = useMemo(() => (res && res.hits.length ? [] : searchPriceRows(prices, q)), [res, prices, q]);
  if (!res) return null;
  const qtyText = (qty) => Object.entries(qty).map(([u, n]) => `${r3(n)}${U(u)}`).join(' · ');
  const nm = (x) => { const np = shownParts(x, byCode); return zh ? (np.zh || np.ko) : np.ko; };
  const sub = (x) => { const np = shownParts(x, byCode); return zh ? (np.zh ? np.ko : '') : np.zh; };
  return (
    <div data-rcv-search>
      <div style={{ ...card, padding: 12, marginBottom: 10, background: '#FFFBF5', borderColor: '#FED7AA' }} data-rcv-search-summary>
        {res.groups.length ? res.groups.map(g => (
          <div key={g.key} data-rcv-search-group={g.item.code || g.item.name} style={{ padding: '4px 0', fontSize: 13 }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}>{nm(g.item)} {sub(g.item) && <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>{sub(g.item)}</span>}</div>
            <div style={{ color: '#3f3f46' }}>
              {B(`입고 ${g.count}번 · ${qtyText(g.qty)} · ${money(g.amount)} · 마지막 ${g.last.slice(5).replace('-', '/')} (${money(g.lastPrice)}/${U(g.item.unit)})`,
                `入库 ${g.count} 次 · ${qtyText(g.qty)} · ${money(g.amount)} · 最近 ${g.last.slice(5).replace('-', '/')}（${money(g.lastPrice)}/${U(g.item.unit)}）`)}
              {g.vendors.length > 0 && <span style={{ color: '#6b7280' }}> · {g.vendors.map(v => vLabel(v)).join(', ')}</span>}
            </div>
          </div>
        )) : (
          <div data-rcv-search-none style={{ fontSize: 14, fontWeight: 700, color: '#3f3f46' }}>{B(`입고 기록에 '${q}'이(가) 없어요`, `入库记录里没有「${q}」`)}</div>
        )}
        {rows.length > 0 && (
          <div data-rcv-search-prices style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
            {B('단가표에는 있어요: ', '单价表里有：')}
            {rows.slice(0, 5).map(p => `${nm(p)}${sub(p) ? `/${sub(p)}` : ''}${p.vendor ? ` (${vLabel(p.vendor)})` : ''}${p.spec ? ` ${p.spec}` : ''}${Number(p.price) > 0 ? ` ${money(p.price)}` : ''}`).join(' · ')}
          </div>
        )}
        <div data-rcv-search-orders style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
          {orders.length
            ? B(`발주 내역: ${orders.length}번 · 최근 ${orders[0].date.slice(5).replace('-', '/')} ${orders[0].vendor ? vLabel(orders[0].vendor) + ' ' : ''}${r3(orders[0].qty)}${U(orders[0].unit)}`,
              `订货记录：${orders.length} 次 · 最近 ${orders[0].date.slice(5).replace('-', '/')} ${orders[0].vendor ? vLabel(orders[0].vendor) + ' ' : ''}${r3(orders[0].qty)}${U(orders[0].unit)}`)
            : B('발주 내역에도 없어요', '订货记录里也没有')}
        </div>
      </div>
      {res.hits.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 6 }}>
          {res.hits.map(({ r, idx }) => (
            <EntryCard key={r.id} r={r} hl={idx} showDate isOpen={open === r.id} onToggle={() => setOpen(open === r.id ? null : r.id)} {...cardProps} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── 📒 외상 ──────────────────────────────────────────────────────────────────
function CreditPane({ credit, receivings, today, Z, zh, money, vLabel, payText, vOne, payOne, onSettle, onUnsettle, openPhoto }) {
  const { B } = Z;
  const [openV, setOpenV] = useState(credit.length === 1 ? credit[0].vendor : null);
  const [off, setOff] = useState([]);           // 정산에서 뺄 영수증 id
  const [method, setMethod] = useState('wechat');
  const [date, setDate] = useState(today);
  const total = credit.reduce((a, c) => a + c.unpaid, 0);
  const settled = useMemo(() => receivings.filter(r => r.payment === 'credit' && r.paid)
    .sort((a, b) => String(b.paidAt || '').localeCompare(String(a.paidAt || '')) || String(b.date).localeCompare(String(a.date))).slice(0, 20), [receivings]);

  return (
    <div>
      <div style={{ ...card, padding: 14, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, background: total > 0 ? '#FEF2F2' : '#F0FDF4', borderColor: total > 0 ? '#FCA5A5' : '#86EFAC' }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>{B('갚을 외상 합계', '待付赊账合计')}</div>
        <div data-rcv-credit-total style={{ marginLeft: 'auto', fontSize: 22, fontWeight: 900, color: total > 0 ? RED : GREEN }}>{money(total)}</div>
      </div>
      {!credit.length && <div style={{ ...card, padding: 20, textAlign: 'center', color: '#6b7280', fontSize: 14 }} data-rcv-credit-empty>{B('갚을 외상이 없어요 👍', '没有待付的赊账 👍')}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 8 }}>
        {credit.map(c => {
          const isOpen = openV === c.vendor;
          const picked = c.list.filter(r => !off.includes(r.id));
          const pickedSum = Math.round(picked.reduce((a, r) => a + (Number(r.total) || 0), 0) * 100) / 100;
          return (
            <div key={c.vendor} data-rcv-credit={c.vendor} style={{ ...card, overflow: 'hidden', borderColor: isOpen ? RED : '#e5e7eb' }}>
              <div onClick={() => { setOpenV(isOpen ? null : c.vendor); setOff([]); }} style={{ padding: '12px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <span style={{ fontWeight: 800, color: '#1e40af', fontSize: 15 }}>🏪 {vLabel(c.vendor, c.vendor_zh)}</span>
                <span style={{ fontSize: 12, color: '#6b7280' }}>{c.count}{B('건', ' 笔')} · {B('가장 오래된', '最早')} {c.oldest}</span>
                <span style={{ marginLeft: 'auto', fontSize: 17, fontWeight: 900, color: RED }}>{money(c.unpaid)}</span>
              </div>
              {isOpen && (
                <div style={{ borderTop: '1px solid #f1f1f3', padding: '8px 12px 12px' }}>
                  {c.list.map(r => {
                    const on = !off.includes(r.id);
                    return (
                      <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 14, cursor: 'pointer', borderBottom: '1px solid #f4f4f5' }}>
                        <input type="checkbox" data-rcv-credit-pick={r.id} checked={on} onChange={() => setOff(on ? [...off, r.id] : off.filter(x => x !== r.id))}
                          style={{ width: 18, height: 18, accentColor: RED }} />
                        <span>{dayLabel(r.date, zh)}</span>
                        <span style={{ fontSize: 12, color: '#6b7280' }}>{(r.items || []).length}{B('품목', ' 项')}</span>
                        {r.hasPhoto && <button onClick={(e) => { e.preventDefault(); openPhoto(r.id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}>📷</button>}
                        <span style={{ marginLeft: 'auto', fontWeight: 800 }}>{money(r.total)}</span>
                      </label>
                    );
                  })}
                  <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    {SETTLE_METHODS.map(m => <button key={m} data-rcv-settle-method={m} onClick={() => setMethod(m)} style={chip(method === m, GREEN)}>{payText(m)}</button>)}
                    <input type="date" data-rcv-settle-date value={date} onChange={e => setDate(e.target.value)} style={{ padding: '6px 8px', borderRadius: 7, border: '1px solid #d1d5db', fontSize: 14 }} />
                  </div>
                  <button data-rcv-settle disabled={!picked.length}
                    onClick={() => {
                      if (!window.confirm(Z(`${vOne(c.vendor, c.vendor_zh, 'ko')} 외상 ${picked.length}건 ${money(pickedSum)}을 ${payOne(method, 'ko')}(으)로 ${date}에 정산할까요?`,
                        `用${payOne(method, 'zh')}在 ${date} 结清 ${vOne(c.vendor, c.vendor_zh, 'zh')} 的 ${picked.length} 笔赊账 ${money(pickedSum)} 吗？`))) return;
                      onSettle(picked.map(r => r.id), date, method);
                      setOff([]);
                    }}
                    style={{ ...btn(!!picked.length, GREEN), width: '100%', marginTop: 10, padding: '11px', fontSize: 15, opacity: picked.length ? 1 : 0.5 }}>
                    ✓ {B(`정산 ${picked.length}건 · ${money(pickedSum)}`, `结清 ${picked.length} 笔 · ${money(pickedSum)}`)}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {settled.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 6, color: '#3f3f46' }}>{B('최근 정산', '最近结清')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 5 }}>
            {settled.map(r => (
              <div key={r.id} data-rcv-settled={r.id} style={{ ...card, padding: '8px 12px', display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, color: '#1e40af' }}>{vLabel(r.vendor, r.vendor_zh)}</span>
                <span style={{ color: '#6b7280' }}>{r.date}</span>
                <span style={{ color: GREEN }}>→ {r.paidAt} {payText(r.paidMethod)}</span>
                <span style={{ marginLeft: 'auto', fontWeight: 800 }}>{money(r.total)}</span>
                <button data-rcv-unsettle={r.id} onClick={() => onUnsettle(r.id)} style={{ ...btn(false), fontSize: 11, padding: '3px 8px' }}>{B('되돌리기', '撤销')}</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── V159 계정과목 ─────────────────────────────────────────────────────────────
// V172: 둘 다 — 고르기 칸은 한 줄 문자열, 배지는 다른 언어를 작게
const acctText = (a, zh) => { const l = ACCT_LABEL[a] || ACCT_LABEL.etc; return `${l.icon} ${zh ? `${l.zh} · ${l.ko}` : `${l.ko} · ${l.zh}`}`; };
const acctNode = (a, zh) => { const l = ACCT_LABEL[a] || ACCT_LABEL.etc; return <>{l.icon} {zh ? l.zh : l.ko}<span data-bi2 style={SUB_IN}>{zh ? l.ko : l.zh}</span></>; };

function AcctSelect({ value, onChange, zh, dataAttr = 'data-rcv-acct' }) {
  const l = ACCT_LABEL[value] || ACCT_LABEL.food;
  return (
    <select {...{ [dataAttr]: value }} value={value} onChange={e => onChange(e.target.value)} title={zh ? '会计科目 · 계정과목' : '계정과목 · 会计科目'}
      style={{ padding: '4px 6px', borderRadius: 7, border: `1px solid ${value === 'food' ? '#d1d5db' : l.color}`, fontSize: 12, fontWeight: 700,
               color: l.color, background: value === 'food' ? '#fff' : l.bg, maxWidth: 210 }}>
      {ACCTS.map(a => <option key={a} value={a}>{acctText(a, zh)}</option>)}
    </select>
  );
}

function AcctBadge({ acct, zh }) {
  const l = ACCT_LABEL[acct] || ACCT_LABEL.etc;
  return <span data-rcv-acct-badge={acct} style={{ fontSize: 11, fontWeight: 700, color: l.color, background: l.bg, padding: '1px 6px', borderRadius: 5, whiteSpace: 'nowrap' }}>{acctNode(acct, zh)}</span>;
}

/** 계정과목별 금액 — onlyMixed면 두 가지 이상일 때만 */
function AcctBreakdown({ amounts, money, zh, onlyMixed = false }) {
  const keys = ACCTS.filter(a => Number(amounts[a]) > 0);
  if (!keys.length || (onlyMixed && keys.length < 2)) return null;
  return (
    <div data-rcv-acct-breakdown style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8, fontSize: 12 }}>
      {keys.map(a => {
        const l = ACCT_LABEL[a];
        return <span key={a} data-rcv-acct-sum={a} style={{ color: l.color, background: l.bg, padding: '3px 8px', borderRadius: 6, fontWeight: 700 }}>{acctNode(a, zh)} {money(amounts[a])}</span>;
      })}
    </div>
  );
}

/** 새 품목 기본 계정과목 — 이 영수증에서 마지막으로 넣은 새 품목과 같게 (숯 영수증이면 계속 연료) */
function lastNewAcct(draft) {
  const last = [...(draft.lines || [])].reverse().find(l => l.isNew);
  return last ? acctOfLine(last) : 'food';
}
